import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Badge from "../../../../../components/Badge";
import { formatAmount, formatDollarAmount } from "../../../../../utils/methods";
import { TransactionListItem } from "../../../../../interfaces/managerStatement.interface";

interface PaidToManagerCalculationModalContentProps {
  item: TransactionListItem | null;
}

const toMoney = (value: number | null | undefined) => formatDollarAmount(formatAmount(Number(value || 0).toFixed(2)));

const PaidToManagerCalculationModalContent: React.FC<PaidToManagerCalculationModalContentProps> = ({ item }) => {
  const { t } = useTranslation();
  const base = 'reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.calcModal';

  if (!item) return null;

  const paidToManager = Number(item.paid_to_manager || 0);

  const field = (label: string, value: React.ReactNode) => (
    <div className="col-6">
      <div className="text-heading text-uppercase small">{label}</div>
      <div className="fw-semibold">{value}</div>
    </div>
  );

  return (
    <div className="py-2">
      <div className="text-heading text-uppercase small fw-semibold mb-2">{t(`${base}.detailsHeading`)}</div>
      <div className="row g-3 mb-4">
        {field(t(`${base}.paidBy`), item.paid_by_label || '-')}
        {field(t(`${base}.actualCost`), toMoney(item.actual_amount))}
        {field(t(`${base}.totalAmount`), toMoney(item.total_amount))}
        {field(t(`${base}.preTaxAmount`), toMoney(item.sub_total_amount ?? item.total_amount))}
        {item.claim_amount_received != null && field(t(`${base}.claimAmountReceived`), toMoney(item.claim_amount_received))}
        {field(t(item.claim_amount_received != null ? `${base}.marginClaim` : `${base}.margin`), toMoney(item.margin_amount))}
      </div>

      <div className="bg-body border border-primary rounded p-3 mb-4 d-flex justify-content-between align-items-center">
        <span className="text-heading fw-semibold">{t(`${base}.paidToManager`)}</span>
        <span
          className={clsx('h5 mb-0 fw-bold', {
            'text-heading': paidToManager >= 0,
            'text-danger': paidToManager < 0,
          })}
        >
          {toMoney(paidToManager)}
        </span>
      </div>

      <div className="bg-label-warning rounded border-start border-warning border-4 p-3" role="note">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-heading text-uppercase small fw-semibold">
            <i className="ri-information-line ri-16px me-1" aria-hidden="true" />
            {t(`${base}.ruleHeading`)}
          </span>
          {item.rule_label && <Badge text={item.rule_label} variant="warning" />}
        </div>
        <div className="text-heading fw-semibold mb-1">{item.rule_name}</div>
        <div className="text-heading small">{item.rule_formula}</div>
      </div>
    </div>
  );
};

export default PaidToManagerCalculationModalContent;
